import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";
import { useLeaderboardQuery } from "@/api/vercel";
import { format } from "date-fns";
import { RaceCountdownProvider } from "@/contexts/RaceCountdownContext";
import NextToJumpCarousel from "../NextToJumpCarousel";
import BottomNavbar from "../bottom-navbar";
// Add CSS to override the tips container positioning
import "./RacingLayout.css";

// Create a context to share the carousel reference across components
export const CarouselContext = createContext(null);

export const useCarousel = () => useContext(CarouselContext);

export default function RacingLayout() {
  const carouselRef = useRef(null);
  const [hasRacesToday, setHasRacesToday] = useState(false);
  const [isRaceNotFound, setIsRaceNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const today = format(new Date(), "dd-MM-yyyy");
  const location = useLocation();
  const isVipRoute = location.pathname === "/vip";
  const layoutWidthClass = isVipRoute ? "max-w-none md:max-w-5xl" : "max-w-md";

  // Get leaderboard data for race existence checks
  const { data: { data: leaderboardQueryData } = {} } = useLeaderboardQuery({
    date: today,
    collection: "predictions",
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Function to handle next-to-jump data loading
  const handleDataLoad = (hasRaces) => {
    setHasRacesToday(hasRaces);
    setIsLoading(false);

    // Check if races should exist but aren't found
    if (
      !hasRaces &&
      leaderboardQueryData &&
      Object.keys(leaderboardQueryData).length > 0
    ) {
      setIsRaceNotFound(true);
    } else {
      setIsRaceNotFound(false);
    }
  };

  // Function to handle next-to-jump navigation
  const handleNextToJump = () => {
    if (carouselRef.current && carouselRef.current._getNextAvailableRace) {
      const nextRace = carouselRef.current._getNextAvailableRace();
      if (nextRace) {
        carouselRef.current.navigateToNextRace();
      }
    }
  };

  return (
    <CarouselContext.Provider
      value={{
        ref: carouselRef,
        hasRaces: hasRacesToday,
        isRaceNotFound,
        isLoading,
        nextToJump: handleNextToJump,
      }}
    >
      <RaceCountdownProvider>
        <div className="h-full md:min-h-screen bg-gray-50 flex flex-col relative racing-layout">
          <div
            className={`race-container-wrapper ${layoutWidthClass} mx-auto w-full flex-shrink-0`}
          >
            {/* Persistent NextToJumpCarousel that stays mounted across page changes */}
            <div className="race-filter-wrapper">
              <NextToJumpCarousel
                ref={carouselRef}
                onDataLoad={handleDataLoad}
              />
            </div>
          </div>

          {/* Outlet container with flex-grow to take remaining height */}
          <div
            className={`flex-grow ${layoutWidthClass} mx-auto w-full overflow-y-auto bg-white`}
          >
            <Outlet
              context={{
                carouselRef,
                hasRacesToday,
                isRaceNotFound,
                handleNextToJump,
              }}
            />
          </div>

          {/* Bottom navbar with flex-shrink-0 to ensure it doesn't get squished */}
          <div className="flex-shrink-0">
            <BottomNavbar
              carouselRef={carouselRef}
              onNextToJump={handleNextToJump}
            />
          </div>
        </div>
      </RaceCountdownProvider>
    </CarouselContext.Provider>
  );
}
